// src/components/admin_panel/AdminMeetupGroupMappings.js
import React, { useState, useEffect, useCallback } from "react";
import { adminApi } from "../../services/adminApi";

const STATUS_COLORS = {
  unmatched: { bg: "#fef3c7", text: "#92400e", label: "Needs Review" },
  auto: { bg: "#dbeafe", text: "#1e40af", label: "Auto-matched" },
  manual: { bg: "#dcfce7", text: "#166534", label: "Manual" },
};

const TH_STYLE = { padding: "10px 12px", textAlign: "left", fontWeight: "600", borderBottom: "2px solid #e5e7eb" };
const TD_STYLE = { padding: "10px 12px", borderBottom: "1px solid #eee", verticalAlign: "top" };

function formatPct(score) {
  return score == null ? null : `${Math.round(score * 100)}%`;
}

function StatusBadge({ matchType, score }) {
  const c = STATUS_COLORS[matchType] || STATUS_COLORS.unmatched;
  const pct = formatPct(score);
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: "999px",
      fontSize: "0.75rem", fontWeight: 700, background: c.bg, color: c.text, whiteSpace: "nowrap",
    }}>
      {c.label}{pct ? ` (${pct})` : ""}
    </span>
  );
}

// One row's "map to" control - a dropdown of every known Meetup group plus
// a "no group" option, defaulting to whatever's currently applied
// (mapping.meetup_urlname) or, failing that, the auto-suggested candidate
// (so picking a value and hitting Save is a one-click accept of the
// suggestion in the common case, not a blank dropdown the admin has to
// fill in from scratch even when we already have a good guess).
function MappingRow({ mapping, knownMeetupGroups, onAction, busy }) {
  const [selected, setSelected] = useState(
    mapping.meetup_urlname || mapping.suggested_meetup_urlname || ""
  );

  useEffect(() => {
    setSelected(mapping.meetup_urlname || mapping.suggested_meetup_urlname || "");
  }, [mapping.meetup_urlname, mapping.suggested_meetup_urlname]);

  const hasChanged = selected !== (mapping.meetup_urlname || "");
  const suggestedPct = formatPct(mapping.suggested_score);

  return (
    <tr>
      <td style={TD_STYLE}><strong>{mapping.source_group}</strong></td>
      <td style={{ ...TD_STYLE, textAlign: "center" }}>{mapping.subscription_count}</td>
      <td style={TD_STYLE}><StatusBadge matchType={mapping.match_type} score={mapping.match_score} /></td>
      <td style={TD_STYLE}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={busy}
            style={{ padding: "6px", borderRadius: "6px", border: "1px solid #d1d5db", minWidth: "220px" }}
          >
            <option value="">— No group —</option>
            {knownMeetupGroups.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          {mapping.match_type === "unmatched" && mapping.suggested_meetup_urlname && (
            <span style={{ fontSize: "0.75rem", color: "#92400e" }}>
              Suggested: {mapping.suggested_meetup_urlname}{suggestedPct ? ` (${suggestedPct} match)` : ""}
            </span>
          )}
        </div>
      </td>
      <td style={TD_STYLE}>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <button
            onClick={() => onAction(mapping.source_group, selected ? "set" : "unmap", selected || undefined)}
            disabled={busy || (!hasChanged && mapping.match_type !== "unmatched")}
            style={{
              padding: "6px 12px", borderRadius: "6px", border: "none", fontSize: "0.8rem", fontWeight: 600,
              background: busy ? "#a5b4fc" : "#667eea", color: "white",
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {selected ? "Save" : "Confirm no group"}
          </button>
          {mapping.match_type === "manual" && (
            <button
              onClick={() => onAction(mapping.source_group, "reset")}
              disabled={busy}
              title="Clear this manual override and let auto-matching re-derive it next load"
              style={{
                padding: "6px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "0.8rem",
                background: "white", color: "#374151", cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              Reset to auto
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

const AdminMeetupGroupMappings = ({ adminToken, tabName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busySourceGroup, setBusySourceGroup] = useState(null);

  const fetchData = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    setError("");
    try {
      const result = await adminApi.getSourceGroupMappings(adminToken);
      setData(result);
    } catch (err) {
      console.error("Error fetching source group mappings:", err);
      setError(err.message || "Failed to load Meetup Group Mappings");
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function handleAction(sourceGroup, action, meetupUrlname) {
    setBusySourceGroup(sourceGroup);
    try {
      await adminApi.updateSourceGroupMapping(sourceGroup, action, meetupUrlname, adminToken);
      await fetchData();
    } catch (err) {
      console.error(`Error applying '${action}' to ${sourceGroup}:`, err);
      setError(err.message || `Failed to update mapping for ${sourceGroup}`);
    } finally {
      setBusySourceGroup(null);
    }
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ marginBottom: "10px", color: "#111827" }}>
        🔗 {tabName || "Meetup Group Mappings"}
      </h2>
      <p style={{ color: "#666", marginBottom: "20px", fontSize: "14px", maxWidth: "820px" }}>
        A subscriber's referral link (Source Group, e.g. "birmingham20s30s") and the Meetup group's
        own landing page slug (e.g. "birmingham-socials-20s-30s") are often written differently for
        the exact same real-world group. This page maps each Source Group tag actually seen in
        Stripe to the Meetup group it refers to, so the Subscriptions tab and Meetup Group Finance
        can credit both to the same group instead of splitting them apart. Every tag is
        auto-matched by how similar it looks to a known group's slug when this page loads — confident
        matches (green/blue) apply immediately; anything less certain (amber) waits here for you to
        confirm or correct.
      </p>

      {data?.summary && (
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px",
          marginBottom: "20px", maxWidth: "640px",
        }}>
          <div style={{ background: "white", borderRadius: "12px", padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Needs Review</div>
            <div style={{ fontSize: "22px", fontWeight: 700, color: data.summary.needs_review > 0 ? "#92400e" : "#111827" }}>
              {data.summary.needs_review}
            </div>
          </div>
          <div style={{ background: "white", borderRadius: "12px", padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Auto-matched</div>
            <div style={{ fontSize: "22px", fontWeight: 700, color: "#1e40af" }}>{data.summary.auto_matched}</div>
          </div>
          <div style={{ background: "white", borderRadius: "12px", padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Manually Confirmed</div>
            <div style={{ fontSize: "22px", fontWeight: 700, color: "#166534" }}>{data.summary.manual}</div>
          </div>
        </div>
      )}

      <button
        onClick={fetchData}
        disabled={loading}
        style={{
          background: loading ? "#a5b4fc" : "#667eea", color: "white", border: "none",
          padding: "9px 20px", borderRadius: "8px", fontSize: "14px", fontWeight: "600",
          cursor: loading ? "not-allowed" : "pointer", marginBottom: "16px",
        }}
      >
        {loading ? "Refreshing…" : "Refresh (re-scan Stripe + re-match)"}
      </button>

      {error && (
        <div style={{
          background: "#fee2e2", border: "1px solid #ef4444", borderRadius: "8px",
          padding: "12px 16px", color: "#991b1b", marginBottom: "16px",
        }}>
          {error}
        </div>
      )}

      {loading && !data && <p>Loading…</p>}

      {data && data.mappings.length === 0 && !loading && (
        <p style={{ color: "#9ca3af" }}>No source_group values found on any Stripe subscription yet.</p>
      )}

      {data && data.mappings.length > 0 && (
        <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", minWidth: "820px" }}>
            <thead>
              <tr>
                <th style={TH_STYLE}>Source Group</th>
                <th style={{ ...TH_STYLE, textAlign: "center" }}>Subscriptions</th>
                <th style={TH_STYLE}>Status</th>
                <th style={TH_STYLE}>Mapped To</th>
                <th style={TH_STYLE}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.mappings.map((mapping) => (
                <MappingRow
                  key={mapping.source_group}
                  mapping={mapping}
                  knownMeetupGroups={data.known_meetup_groups}
                  onAction={handleAction}
                  busy={busySourceGroup === mapping.source_group}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminMeetupGroupMappings;
